import { useState } from "react";
import api, { apiError, brl, parseMoney } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input } from "@/components/ui-kit";
import { Scale } from "lucide-react";

/**
 * Ajuste de caixa a partir da CONTAGEM.
 *
 * O operador digita quanto há na gaveta, não a diferença. Pedir a diferença
 * obrigaria a fazer de cabeça uma subtração contra um "esperado" lido noutro
 * canto da tela — e um erro nessa conta vira exatamente o furo que o ajuste
 * existe para fechar.
 *
 * A prévia da diferença aparece enquanto se digita, porque o número que importa
 * para quem confere não é o total contado (esse ele acabou de contar), é o
 * quanto faltou ou sobrou.
 */
export default function AdjustDialog({ expected, onClose, onDone }) {
  const [counted, setCounted] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const valor = parseMoney(counted);
  const delta = counted === "" ? null : Math.round((valor - expected) * 100) / 100;
  const semDiferenca = delta !== null && Math.abs(delta) < 0.01;

  const save = async () => {
    if (counted === "") { toast.error("Informe quanto há na gaveta"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/pos/cash-session/adjust", {
        counted_amount: valor, reason: reason || null,
      });
      toast.success(`Ajuste de ${brl(Math.abs(data.delta))} registrado — a gaveta passa a bater.`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Ajuste de caixa" testId="cash-adjust-dialog">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Conte o dinheiro da gaveta e informe o total. A diferença entra no extrato como
          ajuste, com o seu nome — e a partir daí o esperado passa a ser o que você contou.
        </p>

        <div className="flex justify-between text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <span className="text-slate-600">Esperado agora</span>
          <span className="font-mono font-semibold text-slate-900">{brl(expected)}</span>
        </div>

        <Field label="Contado na gaveta (R$)" required>
          <Input type="number" step="0.01" min="0" value={counted} onChange={(e) => setCounted(e.target.value)}
            placeholder="0,00" data-testid="cash-adjust-counted" autoFocus mono />
        </Field>

        {delta !== null && (
          <div className={`rounded-lg px-3 py-2.5 text-sm ${
            semDiferenca ? "bg-slate-50 border border-slate-200 text-slate-600"
              : delta < 0 ? "bg-red-50 border border-red-200 text-red-700"
                : "bg-amber-50 border border-amber-200 text-amber-800"
          }`} data-testid="cash-adjust-delta">
            {semDiferenca
              ? "A contagem bate com o esperado — não há o que ajustar."
              : <>
                  {delta < 0 ? "Faltam" : "Sobram"}{" "}
                  <b className="font-mono">{brl(Math.abs(delta))}</b> na gaveta.
                </>}
          </div>
        )}

        <Field label="Motivo" hint="Opcional — mas é o que explica a quebra depois.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: troco dado a mais" data-testid="cash-adjust-reason" />
        </Field>

        <Button onClick={save} loading={saving} disabled={counted === "" || semDiferenca}
          icon={Scale} size="lg" block data-testid="cash-adjust-save">
          Registrar ajuste
        </Button>
      </div>
    </Modal>
  );
}
